/* eslint-disable indent */
import Card from '@/components/Card';
import { useQueryClient } from '@tanstack/react-query';
import { ResultField } from './ResultField';
import { ClickableVideoItem } from './ClickableVideoItem';
import { useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { useGetVehiculeCheckVideo } from '@/hooks/vehicule-check-result.hooks';
import DetectionVideoPlayer from './DetectionVideoPlayer';
import Loader from '@/components/Loading/Loader';
import { TFunction } from 'i18next';
import { useGetTranslatedCheckPoint } from '@/hooks/translated-check-point.hooks';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { VehiculeCheckProgressIndicator } from '@/components/Custom/ProgressIndicator/VehiculeCheckProgressIndicator';
import { twMerge } from 'tailwind-merge';
import { useVehiculeCheckVideoSockets } from '@/hooks/sockets/vehicule-check-video-sockets.hooks';
import { useGetResultVideoChapters } from '../hooks/result-video-chapters.hooks';

interface VehiculeCheckResultsVideosProps {
  t: TFunction<undefined>;
  isLoading?: boolean;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
}

export const VehiculeCheckResultsVideos = ({
  t,
  isLoading,
  vehiculeCheckResult,
}: VehiculeCheckResultsVideosProps) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);

  const resultContainsEquipments = vehiculeCheckResult?.results?.some(
    (item) => item?.checkPoint?.equipments?.length > 0,
  );
  const resultContainsDamages = vehiculeCheckResult?.results?.some(
    (item) => item?.checkPoint?.damages?.length > 0,
  );

  // Get video url
  // const videoUrl = '/video_result.mp4';
  const {
    uncachedVideoUrl,
    videoUrl,
    isLoading: isLoadingVideo,
  } = useGetVehiculeCheckVideo(id);

  const { getTranslatedCheckPoint } = useGetTranslatedCheckPoint();

  const { videoChapterData } = useGetResultVideoChapters({
    t,
    isLoading,
    vehiculeCheckResult,
    getTranslatedCheckPoint,
  });

  /**
   * === Hook for Vehicule Check Video Sockets ===
   */
  const {
    vehiculeCheckProgressStatus,
    videoUploadProgressPercent,
    isVideoUploading,
  } = useVehiculeCheckVideoSockets({
    vehiculeCheckId: id,
  });
  /**
   * === END OF Hook for Vehicule Check Video Sockets ===
   */

  // Ensure videoUrl is re-checked once processing completes (avoid needing a page refresh)
  useEffect(() => {
    if (!isVideoUploading && id) {
      queryClient.invalidateQueries({
        queryKey: ['vehiculeCheckVideo', uncachedVideoUrl],
      });
    }
  }, [isVideoUploading, id, uncachedVideoUrl]);

  const goToTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play();
    }
  };

  const renderCheckPoints = () => {
    if (!isLoading && vehiculeCheckResult?.results?.length) {
      return vehiculeCheckResult?.results?.map((item) => {
        // Get translated check point
        const translatedCheckPoint = getTranslatedCheckPoint(
          item?.checkPoint?.id,
        );

        return (
          item?.checkPoint?.isVideo && (
            <ClickableVideoItem
              key={item?.checkPoint?.id}
              text={translatedCheckPoint?.name || item?.checkPoint?.name}
              onClick={() => {
                goToTime(item?.checkPoint?.startTimeInVideo || 0);
              }}
            />
          )
        );
      });
    }

    return <></>;
  };

  return (
    <div>
      <Card
        title={
          <TitleSection
            text={t('common:video')}
            defaultIconClassName="ki-youtube"
          />
        }
        bodyClassName={twMerge(
          'relative min-h-[30vh] py-8 flex flex-col items-center gap-5',
          isVideoUploading && 'min-h-0 block',
        )}
      >
        <>
          {isVideoUploading ? (
            <VehiculeCheckProgressIndicator
              t={t}
              progress={videoUploadProgressPercent}
              vehiculeCheckProgressStatus={vehiculeCheckProgressStatus}
            />
          ) : (
            <div className="w-full min-[1400px]:px-[80px] rounded-lg flex flex-col justify-center">
              {!isLoading &&
                !isLoadingVideo &&
                vehiculeCheckResult?.results?.length && (
                  <>
                    <DetectionVideoPlayer
                      t={t}
                      videoRef={videoRef}
                      videoUrl={videoUrl}
                      isLoadingVideo={isLoadingVideo}
                      detectionFiles={{
                        detectionsUrl: resultContainsEquipments
                          ? `${R2_PUBLIC_DOMAIN}/videos/${id}-detection.txt`
                          : undefined,
                        damagesUrl: resultContainsDamages
                          ? `${R2_PUBLIC_DOMAIN}/videos/${id}-damage.txt`
                          : undefined,
                      }}
                      chapterData={videoChapterData}
                      toggleTextClassName="font-medium"
                      videoClassName="h-[50vh]"
                    />
                    {videoUrl && (
                      <>
                        <ResultField
                          containerClassName="mt-5 mb-3 p-0 bg-transparent"
                          textClassName="w-auto text-lg text-black font-bold"
                          text={`${t('accessDirectlyToACheckPoint')} :`}
                        />
                        <div className="grid grid-cols-4 gap-2 max-1.5xl:grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1">
                          {renderCheckPoints()}
                        </div>
                      </>
                    )}
                  </>
                )}
            </div>
          )}
          {(isLoading || isLoadingVideo) && (
            <Loader containerClassName="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          )}
        </>
      </Card>
    </div>
  );
};
